import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Sort } from '@angular/material/sort';

import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { Subject, Subscription, takeUntil } from 'rxjs';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { HasTitle } from '../../../../has-title';
import { AuthService, Session } from '../../../../providers/bridge-api/auth.service';
import { GroupService } from '../../../../providers/semantria-api/group.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from '../../../../providers/semantria-api/semantria-users.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { UserCreateDialogComponent } from '../../components/user-create-dialog/user-create-dialog.component';

import { UserListService } from '../../services/user-list.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  standalone: false,
})
export class UserListComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() isAdmin: boolean;

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;

  private session: Session;

  isLoading = true;
  isLoadingPage = false;
  isSaving: boolean;

  allUsers: SemantriaUser[] = [];
  _users: SemantriaUser[] = [];
  filteredUsers: SemantriaUser[] = [];
  _selectedUser: SemantriaUser;

  dataSource: TableVirtualScrollDataSource<SemantriaUser>;
  displayedColumns = ['email', 'admin'];
  sort: Sort;

  unsub$ = new Subject<void>();
  searchValue: string = '';

  usersRightPlaceholder = { title: '', subtitle: '' };

  page = 0;
  totalPages = 0;
  totalItems = 0;
  private translationSubscription: Subscription;

  constructor(
    protected titleService: Title,
    private groupService: GroupService,
    private userService: SemantriaUsersService,
    private authService: AuthService,
    private dialogService: DialogService,
    private alertService: AlertService,
    private userListService: UserListService,
    private translocoService: TranslocoService
  ) {
    super(titleService);

    this.dataSource = new TableVirtualScrollDataSource([]);
    this.sort = {
      active: 'email',
      direction: 'asc',
    };
  }

  async ngOnInit() {
    this.translationSubscription = this.translocoService
      .selectTranslateObject('user-list')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translatedValue) => {
        this.setTitle(translatedValue['users']);
        this.usersRightPlaceholder = {
          title: translatedValue['right-title'],
          subtitle: translatedValue['right-subtitle'],
        };
      });

    this.userListService.userDeleted.pipe(takeUntil(this.unsub$)).subscribe((user) => {
      this.isSaving = false;

      this.users = this.users.filter((u) => u.user_id !== user.user_id);
      this.filteredUsers = this.filteredUsers.filter((u) => u.user_id !== user.user_id);
      this.dataSource.data = this.filteredUsers;

      if (user === this.selectedUser) {
        if (this.filteredUsers.length > 0) {
          this.selectedUser = this.filteredUsers[0];
        } else {
          this.selectedUser = undefined;
        }
      }

      this.alertService.showToast(
        this.translocoService.translate('user-list.delete-prompt.delete-success', {
          firstName: user.first_name,
          lastName: user.last_name,
        })
      );
    });

    this.userListService.refreshSelectedUser.pipe(takeUntil(this.unsub$)).subscribe(async () => {
      this.selectedUser = await this.userService.getUser(this.selectedUser.user_id);
      let idx = this.users.findIndex((u) => u.user_id === this.selectedUser.user_id);
      this.users.splice(idx, 1, this.selectedUser);

      idx = this.filteredUsers.findIndex((u) => u.user_id === this.selectedUser.user_id);
      this.filteredUsers.splice(idx, 1, this.selectedUser);
      this.dataSource.data = this.filteredUsers;
    });

    this.userListService.refreshAllUsers
      .pipe(takeUntil(this.unsub$))
      .subscribe(() => this.refreshUsers());

    this.page = 0;

    await this.loadUsers();
    this.selectedUser = this.filteredUsers[0];

    this.session = await this.authService.getSession();

    this.isLoading = false;
  }

  ngOnDestroy() {
    if (this.unsub$) {
      this.unsub$.next();
    }
    this.translationSubscription?.unsubscribe();
  }

  async refreshUsers(skipLoad = false) {
    if (!skipLoad) {
      this.isLoading = true;
    }
    this.page = 0;
    await this.loadUsers();

    if (this.selectedUser) {
      this.allUsers.forEach((user) => {
        if (user.user_id === this.selectedUser.user_id) {
          this.selectedUser = user;
        }
      });
    }
    this.isLoading = false;
  }

  onSortChanged($event = this.sort) {
    this.sort = $event;

    const direction = this.sort.direction === 'asc' ? 1 : -1;
    const propSort = (a, b, prop) => {
      if (!a[prop]) {
        if (!b[prop]) {
          return 0;
        }

        return -1 * direction;
      } else if (!b[prop]) {
        return direction;
      }

      return a[prop].localeCompare(b[prop]) * direction;
    };

    this.filteredUsers.sort((u1: any, u2: any) => {
      switch (this.sort.active) {
        case 'email':
          return propSort(u1, u2, 'email');
        case 'name':
          let ret = propSort(u1, u2, 'first_name');
          if (!ret) {
            ret = propSort(u1, u2, 'last_name');
          }
          if (!ret) {
            ret = propSort(u1, u2, 'login');
          }
          return ret;
        case 'admin':
          const u1Admin = SemantriaUsersService.isAccountAdmin(u1);
          const u2Admin = SemantriaUsersService.isAccountAdmin(u2);

          if (u1Admin && !u2Admin) {
            return direction;
          } else if (!u1Admin && u2Admin) {
            return -1 * direction;
          }
          return 0;
        default:
          return 0;
      }
    });

    this.dataSource.data = [...this.filteredUsers];
  }

  async onSearchChanged(value, skipLoad = true) {
    this.searchValue = value;
    await this.refreshUsers(skipLoad);
  }

  onClearSearch(input) {
    input.value = '';
    this.onSearchChanged('', false);
  }

  canCreateUser(): boolean {
    return !this.authService.isReadOnly;
  }

  onCreateUser() {
    this.dialogService
      .showDialog<UserCreateDialogComponent>(UserCreateDialogComponent, {}, {}, false, true)
      .afterClosed()
      .subscribe((user: SemantriaUser) => {
        if (user) {
          this.users.push(user);
          this.filteredUsers.push(user);

          this.onSortChanged();

          this.dataSource.data = this.filteredUsers;

          this.selectedUser = user;
        }
      });
  }

  onUserSelected(user: SemantriaUser) {
    this.authService.extendSession();
    this.selectedUser = user;
  }

  isAccountAdmin(user: SemantriaUser) {
    return SemantriaUsersService.isAccountAdmin(user);
  }

  canChangeUserPermission(user: SemantriaUser): boolean {
    return !(
      this.authService.isReadOnly ||
      !this.isAdmin ||
      this.isSaving ||
      this.isCurrentUser(user)
    );
  }

  async changeUserPermission($event, user: SemantriaUser) {
    this.isSaving = true;

    if ($event.checked) {
      // STEP 1: remove READ_ONLY permissions
      const revokedPermissions = [];
      try {
        const groupReadOnlyPermissions = SemantriaUsersService.getAllReadOnlyPermissions(user);
        for (const permission of groupReadOnlyPermissions) {
          await this.userService.removePermission(user.user_id, permission.permission_id);
          revokedPermissions.push(permission);
        }
      } catch (err) {
        this.isSaving = false;

        for (const permission of revokedPermissions) {
          await this.userService.addPermission(
            user.user_id,
            permission.permission_id,
            permission.group_id
          );
        }

        this.alertService.showErrorToast(
          this.translocoService.translate('user-list.set-admin-failure'),
          err
        );
        return;
      }

      // STEP 2: set ACCOUNT_ADMIN permission
      try {
        await this.userService.addPermission(user.user_id, 'ACCOUNT_ADMIN');
        this.isSaving = false;
        await this.refreshUsers();
      } catch (err) {
        this.isSaving = false;

        this.alertService.showErrorToast(
          this.translocoService.translate('user-list.set-admin-failure'),
          err
        );
      }
    } else {
      let isPermissionRevoked: boolean = false;
      try {
        const adminPermission = SemantriaUsersService.getAccountAdminPermission(user);
        await this.userService.removePermission(user.user_id, adminPermission.permission_id);
        isPermissionRevoked = true;

        const selectedGroupId = this.groupService.getSelectedGroupId();
        await this.groupService.addGroupMember(selectedGroupId, user.user_id);

        this.alertService.showToast(
          this.translocoService.translate('user-list.reassign-to-selected-group-success')
        );
      } catch (err) {
        const errorMsg = isPermissionRevoked
          ? 'user-list.reassign-to-selected-group-failure'
          : 'user-list.revoke-admin-failure';

        this.alertService.showErrorToast(this.translocoService.translate(errorMsg), err);
      } finally {
        this.isSaving = false;
        if (isPermissionRevoked) {
          await this.refreshUsers();
        }
      }
    }
  }

  canDeleteUser(user: SemantriaUser) {
    return !(this.authService.isReadOnly || this.isSaving || this.isCurrentUser(user));
  }

  isCurrentUser(user: SemantriaUser) {
    return this.session.user.id === user.user_id;
  }

  async loadUsers(additive = false) {
    let groupUsers;
    if (this.isAdmin) {
      groupUsers = await this.userService.searchUsers(this.page, 100, this.searchValue);
    } else {
      if (!this.groupService.getSelectedGroup()) {
        await this.groupService.refreshAndSelectActiveGroup();
      }
      const groupId = this.groupService.getSelectedGroupId();
      groupUsers = await this.userService.searchUsers(this.page, 100, this.searchValue, groupId);
    }
    this.totalPages = groupUsers.total_pages;
    this.totalItems = groupUsers.total_elements;
    if (additive) {
      this.allUsers = this.allUsers.concat(groupUsers.data);
    } else {
      if (!this.isAdmin) {
        const adminUsers = await this.userService.getAdmins(0, 100, this.searchValue);
        this.allUsers = [...adminUsers.data, ...groupUsers.data];
      } else {
        this.allUsers = [...groupUsers.data];
      }
    }
    this.users = [...this.allUsers];
    this.filteredUsers = [...this.allUsers];
    this.onSortChanged();
    this.dataSource.data = this.filteredUsers;
  }

  onScrollDown() {
    if (!this.isLoadingPage && this.page + 1 < this.totalPages) {
      const renderedRange = this.virtualScroll.getRenderedRange();
      //manual infinite scrolling.
      const end = renderedRange.end + 10;
      const total = this.allUsers.length;
      if (end > total) {
        this.isLoadingPage = true;
        this.page++;
        this.loadUsers(true).finally(() => {
          this.isLoadingPage = false;
        });
      }
    }
  }

  get users() {
    return this._users;
  }

  set users(userList) {
    this._users = userList;
    this.userListService.usersLoaded(this.users);
  }

  get selectedUser() {
    return this._selectedUser;
  }

  set selectedUser(user) {
    this._selectedUser = user;
    this.userListService.userSelected(this.selectedUser);
  }

  get showEmptySidePane(): boolean {
    return (
      !this.dataSource.data.length && !this.searchValue && !this.allUsers.length && !this.isLoading
    );
  }
}
